// Local Imports
import { logger } from "CoreUtils";
import { graphics, schdata as s, Bbox, layout } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import * as style from "../../style";
import { EntityKind } from "../../entity";
import { Entity, Args } from "../../tree";
import { DrawTo } from "../../draw";
import { type MousePos } from "../../mousepos";
import { hasStack } from "./visibility";
import { drawViaCross } from "./via_pattern";
import { MIN_CULL_SCREEN_PX } from "../../draw/drawto";

export class RouteEntity extends Entity<layout.Route> {
  readonly entityKind = EntityKind.LayoutRoute;
  readonly segments = new Set<SegmentEntity>(); // Segments (exposed for hit-testing)

  constructor(args: Args<layout.Route>) {
    super(args);
    this.build();
  }

  // Build the internal sub-drawers
  // NOTE: this is only called in one place for now, but also will be when we get around to `update`, e.g. to add or remove segments
  override build = (): this => {
    const { net, path } = this.data;

    // Build segments from consecutive point pairs.
    // Skip via segments (duplicate points with different layers).
    // Merge co-linear same-layer segments to avoid visual overlap at shared
    // endpoints (each segment's bbox extends ±width/2 past its endpoints,
    // so adjacent co-linear segments double-render at the junction).
    const segments: Array<Segment> = [];
    for (let i = 0; i < path.length - 1; i++) {
      const p1 = path[i].point;
      const p2 = path[i + 1].point;
      const layerStart = path[i].layer;
      const layerEnd = path[i + 1].layer;
      // Per-segment width: the width of the segment leaving point i lives on
      // that point (outgoing-segment convention). null/undefined = layer default.
      const segWidth = path[i].width;

      // Skip via segments (duplicate points with different layers)
      if (
        p1.x === p2.x &&
        p1.y === p2.y &&
        layerStart.layer !== layerEnd.layer
      ) {
        continue;
      }

      // Wire segment: use start layer (should equal end layer for wires)
      let dir: "horiz" | "vert" | "other";
      if (p1.y === p2.y) {
        dir = "horiz";
      } else if (p1.x === p2.x) {
        dir = "vert";
      } else {
        dir = "other";
      }

      // Merge with previous segment only if co-linear, same layer, AND same
      // width — merging across a width change would erase the narrower/wider
      // sub-segment.
      const prev = segments.length > 0 ? segments[segments.length - 1] : null;
      if (
        prev &&
        prev.dir === dir &&
        prev.layer.layer === layerStart.layer &&
        (prev.width ?? null) === (segWidth ?? null)
      ) {
        // Extend previous segment's endpoint to this segment's endpoint
        prev.points = [prev.points[0], p2];
      } else {
        segments.push({
          net,
          layer: layerStart,
          points: [p1, p2],
          dir,
          width: segWidth,
        });
      }
    }

    segments.forEach((seg) => {
      this.segments.add(new SegmentEntity({ data: seg, parent: this }));
    });

    return this;
  };

  // Assign label visibility: if showLabel is true, the middle segment shows the net label
  assignLabelVisibility = (showLabel: boolean): void => {
    const segArray = Array.from(this.segments);
    const midIdx = Math.floor(segArray.length / 2);
    segArray.forEach((seg, idx) => {
      seg.showLabel = showLabel && idx === midIdx;
    });
  };

  override redraw = (to: DrawTo): unknown => this.redrawIn(to, null);

  // Redraw variant that optionally takes a `localViewport` — the DrawTo's
  // viewport inverse-transformed into this cell's local coordinates. Passed
  // down to each segment so individual segments outside the viewport can be
  // culled (counted as `overlays.routes.culled`). The base `redraw` omits
  // this to satisfy the Entity signature.
  redrawIn = (to: DrawTo, localViewport: Bbox | null): unknown => {
    // Draw route segments
    this.segments.forEach((seg) => seg.redrawIn(to, localViewport));
    // Draw vias at layer transitions
    this.drawVias(to);
    // Draw highlight overlay on each segment when selected
    if (this.highlighted) {
      this.segments.forEach((seg) => {
        to.bbox(seg.bbox, {
          fill: style.Color.new(0, 180, 255, 0.3),
          stroke: style.Color.new(0, 180, 255, 0.8),
          linewidth: 2 / to.scale,
          cap: "round",
          join: "round",
        });
      });
    }
    return null;
  };

  // Draw vias at layer transition points
  // Vias are duplicate points with different layers
  private drawVias = (to: DrawTo): void => {
    const { path } = this.data;
    if (path.length < 2) return; // No transitions possible

    // NOTE: vias are drawn at their ViaDefinition size and are NOT resized to a
    // route's custom width — a wide route currently necks down to a default via
    // at layer transitions. Widening vias (via arrays / enclosure) is deferred to
    // a follow-up; see the route-width feature notes.

    // Get stack from root for via definitions
    const root = this.root;
    if (!hasStack(root)) return;
    const { stack } = root;
    if (!stack?.vias) return;

    // Get PDK for colors from DrawTo context
    const { pdk } = to;

    // Check for via segments (duplicate points with different layers)
    for (let i = 0; i < path.length - 1; i++) {
      const layerA = path[i].layer;
      const layerB = path[i + 1].layer;
      const p1 = path[i].point;
      const p2 = path[i + 1].point;

      // Skip if same layer (no via needed)
      if (layerA.layer === layerB.layer) continue;

      // Skip if points are different (wire segment, not via)
      if (p1.x !== p2.x || p1.y !== p2.y) continue;

      // Find via definition for this layer pair
      const viaDef = findViaDefinition(stack.vias, layerA.layer, layerB.layer);
      if (!viaDef) {
        logger.warn(
          `[Via] No via definition for layer transition ${layerA.layer} -> ${layerB.layer}`
        );
        continue;
      }

      // Check layer visibility - skip if via layer is hidden
      if (to.hiddenLayers.has(viaDef.layer)) {
        continue;
      }

      // Via location is at the duplicate point
      const loc = p1;

      // Draw the via
      const viaBbox = Bbox.fromSizeAndCenter(
        { width: viaDef.width, height: viaDef.height },
        loc
      );
      const viaStyle = makeViaStyle(viaDef.layer, pdk);
      to.bbox(viaBbox, viaStyle);

      // Draw cross (X) inside the via
      drawViaCross(to, viaBbox, getViaColor(viaDef.layer, pdk));
    }
  };

  // Override clear() so the base-class update() flow (clear → merge data → build)
  // also resets the segments set, preventing stale segment references after a move.
  override clear() {
    super.clear();
    this.segments.clear();
  }

  // Convert back to the serializable data model
  // N-layers model: preserve original data including via segments
  toData = (): layout.Route => {
    // Return the original route data - don't try to reconstruct from filtered segments
    // as that would lose via information
    return this.data;
  };
}

// Route alpha value (slightly more opaque than general layers)
const ROUTE_ALPHA = 0.8;

// Fallback colors for when PDK is not available
const FALLBACK_ROUTE_COLORS = [
  style.Color.new(255, 0, 0, ROUTE_ALPHA), // M1 - Red
  style.Color.new(0, 0, 255, ROUTE_ALPHA), // M2 - Blue
  style.Color.new(0, 255, 255, ROUTE_ALPHA), // M3 - Cyan
  style.Color.new(0, 255, 0, ROUTE_ALPHA), // M4 - Green
  style.Color.new(255, 255, 0, ROUTE_ALPHA), // M5 - Yellow
];

// Get route color from PDK or fallback
function getRouteColor(
  layerNumber: number,
  pdk: schema.pdk.PDK | null
): style.Color {
  // Try to get color from PDK
  if (pdk) {
    const pdkLayer = pdk.layers.find((l) => l.number === layerNumber);
    if (pdkLayer?.color) {
      const { r, g, b } = pdkLayer.color;
      // Use ROUTE_ALPHA for routes (more opaque than general layers)
      return style.Color.new(r, g, b, ROUTE_ALPHA);
    }
    // PDK exists but layer has no color defined
    logger.debug(
      `[RouteColor] Layer ${layerNumber} in PDK ${pdk.id} has no color, using fallback`
    );
  } else {
    // No PDK available
    logger.debug(
      `[RouteColor] No PDK available for layer ${layerNumber}, using fallback`
    );
  }

  // Fallback to color cycling
  return FALLBACK_ROUTE_COLORS[layerNumber % FALLBACK_ROUTE_COLORS.length];
}

function makeRouteStyleTable(
  layerNumber: number,
  pdk: schema.pdk.PDK | null
): style.StyleTable {
  const color = getRouteColor(layerNumber, pdk);

  return {
    shape: {
      fill: color,
      stroke: color,
      linewidth: 2,
      cap: "round",
      join: "round",
    },
    text: {
      family: style.THE_FONT,
      style: "normal",
      weight: 700, // Typical value for "bold"
      size: 255,
      fill: color,
      stroke: color,
      hidden: false,
    },
  };
}

// Via alpha value (same as routes for visual consistency)
const VIA_ALPHA = 0.8;

// Find via definition for a layer pair (order-independent)
function findViaDefinition(
  vias: Array<schema.metal.ViaDefinition>,
  layerA: number,
  layerB: number
): schema.metal.ViaDefinition | undefined {
  // Normalize to (lower, upper) ordering
  const lower = Math.min(layerA, layerB);
  const upper = Math.max(layerA, layerB);

  // Normalize via definition layers too (defensive against malformed data)
  return vias.find((v) => {
    const viaLower = Math.min(v.lowerLayer, v.upperLayer);
    const viaUpper = Math.max(v.lowerLayer, v.upperLayer);
    return viaLower === lower && viaUpper === upper;
  });
}

// Get via color from PDK or fallback
function getViaColor(
  layerNumber: number,
  pdk: schema.pdk.PDK | null
): style.Color {
  if (pdk) {
    const pdkLayer = pdk.layers.find((l) => l.number === layerNumber);
    if (pdkLayer?.color) {
      const { r, g, b } = pdkLayer.color;
      return style.Color.new(r, g, b, VIA_ALPHA);
    }
  }
  // Fallback: use a distinct color for vias (purple-ish)
  return style.Color.new(180, 100, 255, VIA_ALPHA);
}

// Create style for via rendering
function makeViaStyle(
  layerNumber: number,
  pdk: schema.pdk.PDK | null
): style.ShapeStyle {
  const color = getViaColor(layerNumber, pdk);
  return {
    fill: color,
    stroke: color,
    linewidth: 1,
    cap: "round",
    join: "round",
  };
}

interface Segment {
  net: string;
  layer: layout.MetalLayer;
  points: [s.Point, s.Point];
  dir: "horiz" | "vert" | "other";
  // Explicit route width (nm); null/undefined means the layer default.
  width?: layout.RouteWidth;
}

export class SegmentEntity extends Entity<Segment> {
  readonly entityKind = EntityKind.LayoutRoute;

  // Whether this segment should display the net name label (set by RouteEntity.assignLabelVisibility)
  showLabel = false;

  // Get PDK from parent chain (SegmentEntity -> RouteEntity -> Routes -> ImplEntity -> LayoutEntity -> LayoutContent).
  // Used at construction time to build the style table. At draw time, use to.pdk instead.
  private getPdk(): schema.pdk.PDK | null {
    // `root` is the render tier (RenderContentBase), which exposes `pdk`
    // (populated only by layout content). At draw time, use to.pdk instead.
    return this.root.pdk;
  }

  /**
   * Check if the layer is visible based on the DrawTo's hiddenLayers set.
   *
   * Note: Unlike ImplEntity.isLayerVisible which receives encoded layer values,
   * segment data stores the layer number directly in this.data.layer.layer
   * (already decoded), so no decoding is needed here.
   *
   * @param to - The DrawTo context carrying the hiddenLayers set
   * @returns True if the layer should be rendered, false if hidden
   */
  private isLayerVisible(to: DrawTo): boolean {
    if (to.hiddenLayers.size === 0) return true;
    // this.data.layer.layer is already the raw layer number, no decoding needed
    return !to.hiddenLayers.has(this.data.layer.layer);
  }

  // v6: Use GenAlpha layer ID directly for styling, with PDK colors
  readonly table = makeRouteStyleTable(this.data.layer.layer, this.getPdk());
  readonly shapeStyle = this.makeCache<style.ShapeStyle>(this.table.shape);
  readonly textStyle = this.makeCache<style.TextStyle>(this.table.text);

  readonly calcBbox = () => {
    const { layer, points, dir, width } = this.data;
    const [p0, p1] = points;
    // Effective width: the route's explicit width if set, else the layer default.
    const w = layout.effectiveRouteWidth(width, layer);

    // Use the actual segment direction (computed from point positions), not the PDK layer's
    // preferred routing direction. This ensures horizontal segments get a wide+short bbox
    // and vertical segments get a narrow+tall bbox, regardless of the layer's preferred direction.
    if (dir === "horiz") {
      const xmin = Math.min(p0.x, p1.x);
      const xmax = Math.max(p0.x, p1.x);
      const { y } = p1;
      return Bbox.fromTwoPoints([
        { x: xmax + w / 2, y: y + w / 2 },
        { x: xmin - w / 2, y: y - w / 2 },
      ]);
    }

    // Note we ignore the "other" non-vertical, non-horizontal direction; that falls in here with vertical shapes.
    const ymin = Math.min(p0.y, p1.y);
    const ymax = Math.max(p0.y, p1.y);
    const { x } = p1;
    return Bbox.fromTwoPoints([
      { x: x + w / 2, y: ymax + w / 2 },
      { x: x - w / 2, y: ymin - w / 2 },
    ]);
  };
  readonly bbox: s.Bbox = this.calcBbox();

  // Child element drawers
  readonly labelData = (): graphics.Text & {
    labelKind: s.ExprKind;
  } => ({
    ...graphics.Text.new({
      text: this.data.net, // Net name
      loc: Bbox.center(this.bbox), // Location at center
      alignment: "center",
      baseline: "middle",
      rotation: this.data.dir === "vert" ? 90 : 0,
      height: 2000,
      hidden: false,
    }),
    labelKind: s.ExprKind.PortName, // Text label (with kind)
  });

  // Hit-test: check if the mouse position falls within this segment's bounding box.
  // Route segments are drawn in world coordinates (no xform), so we just check bbox containment.
  // toleranceWorld expands the bbox in all directions (in world/design units) to make thin
  // routes easier to click — routes are nanometer-scale width but rendered with a min pixel size.
  hitTest = (mousePos: MousePos, toleranceWorld: number = 0): boolean => {
    const bbox =
      toleranceWorld > 0
        ? {
            min: {
              x: this.bbox.min.x - toleranceWorld,
              y: this.bbox.min.y - toleranceWorld,
            },
            max: {
              x: this.bbox.max.x + toleranceWorld,
              y: this.bbox.max.y + toleranceWorld,
            },
          }
        : this.bbox;
    return Bbox.contains(bbox, mousePos.sch);
  };

  override redraw = (to: DrawTo): unknown => this.redrawIn(to, null);

  // Redraw variant that optionally takes a `localViewport` — the DrawTo's
  // viewport inverse-transformed into this cell's local coordinates. If the
  // segment bbox is entirely outside the local viewport, it is culled and
  // counted as `overlays.routes.culled`.
  redrawIn = (to: DrawTo, localViewport: Bbox | null): unknown => {
    // Skip drawing if layer is hidden
    if (!this.isLayerVisible(to)) {
      return null;
    }

    // Viewport cull: if this segment is entirely off-screen in the cell's
    // local coordinates, skip drawing. The parent instance's viewport check
    // already eliminated the common case; this trims individual routes when
    // zoomed into part of a big cell.
    if (localViewport && !Bbox.intersects(this.bbox, localViewport)) {
      to.overlayCulled();
      return null;
    }

    // Enforce a minimum 1-pixel width for route segments.
    // Routes are drawn as overlays outside sprites, so we can use the world
    // transform scale to compute the minimum world-coordinate size that
    // produces 1 pixel on screen.
    let drawBbox = this.bbox;
    const { scale } = to;
    if (scale > 0) {
      const minWorld = 1 / scale; // 1 pixel in world coordinates
      const bboxSize = Bbox.size(drawBbox);
      // Cull route segments smaller than MIN_CULL_SCREEN_PX in both dimensions
      if (
        bboxSize.width * scale < MIN_CULL_SCREEN_PX &&
        bboxSize.height * scale < MIN_CULL_SCREEN_PX
      ) {
        to.overlaySizeCulled();
        return null;
      }
      const expandX =
        bboxSize.width < minWorld ? (minWorld - bboxSize.width) / 2 : 0;
      const expandY =
        bboxSize.height < minWorld ? (minWorld - bboxSize.height) / 2 : 0;
      if (expandX > 0 || expandY > 0) {
        drawBbox = {
          min: { x: drawBbox.min.x - expandX, y: drawBbox.min.y - expandY },
          max: { x: drawBbox.max.x + expandX, y: drawBbox.max.y + expandY },
        };
      }
    }

    to.bbox(drawBbox, this.shapeStyle.current);
    if (this.showLabel && to.layoutDisplay?.routeLabels) {
      to.text(this.labelData(), this.textStyle.current, this);
    }
    return null;
  };
}
